<script lang="ts">
	// UC-9. The menu every shop sells from: categories down the side, the
	// chosen category's items beside them with each shop's own price and
	// availability, and an item's details in a Sheet. The Options tab
	// (UC-10) is its own panel, MenuOptions.
	//
	// A shop's cell is empty when it takes the base value; the Sheet sets or
	// clears it. A price is read as it is written (owner/price.svelte.ts).
	//
	// Every save waits in the menu draft until the owner publishes it (UC-38,
	// the bar above). Before a save writes a price it asks the server which
	// prices are far from the ones they replace, and a second sheet names
	// them for the owner to confirm (PriceCheck).
	//
	// An item's description and photo (UC-9.R15, R16) are edits like any
	// other: a picked photo is shrunk in the browser at once (shrinkPhoto),
	// waits in the sheet and is sent once the item is saved (ItemPhoto).

	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Card from '../components/Card.svelte';
	import Chip from '../components/Chip.svelte';
	import Field from '../components/Field.svelte';
	import Loading from '../components/Loading.svelte';
	import NavItem from '../components/NavItem.svelte';
	import Row from '../components/Row.svelte';
	import Segmented from '../components/Segmented.svelte';
	import Sheet from '../components/Sheet.svelte';
	import TablePanel from '../components/TablePanel.svelte';
	import MenuOptions from './MenuOptions.svelte';
	import PriceCheck from './PriceCheck.svelte';
	import ItemPhoto, {
		descriptionEdit,
		photoOutcome,
		savePhoto,
		type PhotoProblem,
	} from './ItemPhoto.svelte';
	import type { Picked } from '../components/ImageInput.svelte';
	import { shrinkPhoto } from './shrinkPhoto';
	import { api } from '../api/client';
	import { fill, message, type Language } from '../i18n/messages';
	import { formatMinor } from '../format/money';
	import type { Account } from '../owner/session.svelte';
	import { loadMenu, ownerShops, ownerMenu } from '../owner/data.svelte';
	import { flash, removeWithUndo } from '../owner/notices.svelte';
	import { PriceInput } from '../owner/price.svelte';
	import { checkPrices, type PriceChange, type PriceEntry } from '../owner/draft.svelte';
	import type { components } from '../api/types';

	type Item = components['schemas']['ItemBody'];
	type Category = components['schemas']['CategoryBody'];

	interface Props {
		language: Language;
		account: Account;
	}

	let { language, account }: Props = $props();

	const minorDigits = $derived(account.settings.minorDigits);
	const currency = $derived(account.settings.currencyCode);
	const shops = $derived(ownerShops());
	const menu = $derived(ownerMenu());

	$effect(() => {
		if (!menu.loaded) void loadMenu(message(language, 'loadFailed'));
	});

	let tab = $state<'items' | 'options'>('items');
	let failure = $state('');

	const categories = $derived(
		[...(menu.value.categories ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
	);
	const groups = $derived(menu.value.groups ?? []);
	const items = $derived(menu.value.items ?? []);

	let chosenCategory = $state('');
	const category = $derived(
		categories.find((c) => c.id === chosenCategory) ?? categories[0] ?? null,
	);
	const shown = $derived(
		items
			.filter((item) => item.categoryId === category?.id)
			.sort((a, b) => a.sortOrder - b.sortOrder),
	);

	async function refresh() {
		await loadMenu(message(language, 'loadFailed'));
	}

	// ---------- categories ----------

	let catEdit = $state<{ id: string; name: string } | null>(null);
	let catBusy = $state(false);

	async function saveCategory() {
		if (!catEdit || !catEdit.name.trim() || catBusy) return;
		catBusy = true;
		failure = '';
		try {
			const name = catEdit.name.trim();
			const existing = categories.find((c) => c.id === catEdit?.id);
			const result = existing
				? await api.PATCH('/api/owner/menu/categories/{categoryId}', {
						params: { path: { categoryId: existing.id } },
						body: { name, sortOrder: existing.sortOrder },
					})
				: await api.POST('/api/owner/menu/categories', {
						body: { name, sortOrder: categories.length * 10 },
					});
			if (result.error !== undefined || !result.data) {
				failure = result.error?.message ?? message(language, 'loadFailed');
				return;
			}
			chosenCategory = result.data.id;
			catEdit = null;
			await refresh();
		} catch {
			failure = message(language, 'loadFailed');
		} finally {
			catBusy = false;
		}
	}

	// Moving one place writes the order of every category that moved, as tens,
	// so the list keeps room and no two share a place.
	async function moveCategory(target: Category, step: -1 | 1) {
		const at = categories.indexOf(target);
		const to = at + step;
		if (at < 0 || to < 0 || to >= categories.length || catBusy) return;
		catBusy = true;
		failure = '';
		const order = [...categories];
		[order[at], order[to]] = [order[to]!, order[at]!];
		try {
			for (const [index, c] of order.entries()) {
				const sortOrder = index * 10;
				if (c.sortOrder === sortOrder) continue;
				const { error } = await api.PATCH('/api/owner/menu/categories/{categoryId}', {
					params: { path: { categoryId: c.id } },
					body: { name: c.id === catEdit?.id ? catEdit.name.trim() || c.name : c.name, sortOrder },
				});
				if (error !== undefined) {
					failure = error.message ?? message(language, 'loadFailed');
					break;
				}
			}
			await refresh();
		} catch {
			failure = message(language, 'loadFailed');
		} finally {
			catBusy = false;
		}
	}

	// A category that still holds items is not removed: the server refuses
	// it (CATEGORY_NOT_EMPTY), and saying so here saves the owner ten seconds
	// of an Undo for a removal that was never going to happen. An empty one
	// leaves at once, and the server hears of it when its Undo runs out.
	let heldBy = $state<{ id: string; name: string; count: number } | null>(null);

	function removeCategory(target: Category) {
		if (catBusy) return;
		failure = '';
		const count = items.filter((i) => i.categoryId === target.id).length;
		if (count > 0) {
			heldBy = { id: target.id, name: target.name, count };
			return;
		}
		heldBy = null;
		catEdit = null;
		// The one before it takes the selection, as the page does; the first
		// category hands it to the next one along.
		const at = categories.findIndex((c) => c.id === target.id);
		chosenCategory = (categories[at - 1] ?? categories[at + 1])?.id ?? '';
		const before = menu.value.categories ?? [];
		menu.value = { ...menu.value, categories: before.filter((c) => c.id !== target.id) };
		removeWithUndo(
			`${message(language, 'menuCategoryRemoved')} ${target.name}`,
			async () => {
				try {
					const { error } = await api.POST('/api/owner/menu/categories/{categoryId}/remove', {
						params: { path: { categoryId: target.id } },
					});
					if (error?.code === 'CATEGORY_NOT_EMPTY') {
						// An item was added to it from elsewhere while the Undo ran.
						// The edit box is gone by now, so the page's band says it.
						failure = fill(
							message(language, 'menuCategoryNotEmpty'),
							target.name,
							Number(error.details?.items ?? 0),
						);
					} else if (error !== undefined) {
						failure = error.message ?? message(language, 'loadFailed');
					}
				} catch {
					failure = message(language, 'loadFailed');
				} finally {
					await refresh();
				}
			},
			() => {
				menu.value = {
					...menu.value,
					categories: [...(menu.value.categories ?? []), target],
				};
				chosenCategory = target.id;
			},
		);
	}

	// ---------- the item sheet ----------

	interface ShopRow {
		/** This shop's own price; left empty, the shop takes the base price. */
		price: PriceInput;
		available: 'base' | 'yes' | 'no';
	}

	interface ItemForm {
		id: string;
		name: string;
		price: PriceInput;
		available: boolean;
		categoryId: string;
		groups: string[];
		shops: Record<string, ShopRow>;
		/** Chosen on Add only; an edit shows it and never sends it (UC-9.R13). */
		soldBy: 'piece' | 'weight';
		/** The item's text for diners, line breaks kept (UC-9.R15). */
		description: string;
		/** The description as the sheet opened; an edit sends it only when changed. */
		descriptionWas: string;
		/** The item's photo as saved; empty when it has none. */
		photoUrl: string;
		/** A photo picked in the sheet, shrunk, with its preview, not sent yet. */
		photo: { file: Blob; url: string } | null;
		/** «امسح» was tapped: the saved photo goes when the item is saved. */
		photoCleared: boolean;
	}

	let itemForm = $state<ItemForm | null>(null);
	let itemOpen = $state(false);
	let itemSaving = $state(false);
	let itemFailure = $state('');
	// The prices the server flagged, while the price check's sheet is open,
	// and whether the check itself failed.
	let flagged = $state<PriceChange[]>([]);
	let checkFailed = $state(false);
	let photoProblem = $state<PhotoProblem | null>(null);
	// The pick being shrunk; a later pick, or another sheet, outdates it.
	let picking = $state(0);
	let preparing = $state(false);

	// A picked photo's preview lives only while the sheet holds it.
	function dropPicked() {
		if (itemForm?.photo) URL.revokeObjectURL(itemForm.photo.url);
		if (itemForm) itemForm.photo = null;
	}

	// A refused pick drops the photo picked before it too, so the sheet never
	// shows one photo under another's refusal, and Save never sends it.
	async function pickPhoto(picked: Picked) {
		const form = itemForm;
		const turn = ++picking;
		if (picked.url) URL.revokeObjectURL(picked.url);
		photoProblem = picked.problem;
		preparing = false;
		if (!form) return;
		dropPicked();
		if (picked.problem) return;
		preparing = true;
		const shrunk = await shrinkPhoto(picked.file);
		if (turn !== picking || itemForm !== form) return;
		preparing = false;
		form.photo = { file: shrunk, url: URL.createObjectURL(shrunk) };
		form.photoCleared = false;
	}

	function removePhoto() {
		picking++;
		preparing = false;
		photoProblem = null;
		dropPicked();
		if (itemForm) itemForm.photoCleared = true;
	}

	function closeItem() {
		picking++;
		preparing = false;
		dropPicked();
		itemOpen = false;
	}

	const wording = $derived({
		language,
		minorDigits,
		shopName: (id: string) => shops.value.find((s) => s.id === id)?.name ?? id,
	});

	function openItem(item: Item | null) {
		picking++;
		preparing = false;
		dropPicked();
		const shopsRows: Record<string, ShopRow> = {};
		for (const shop of shops.value) {
			const override = (item?.overrides ?? []).find((o) => o.shopId === shop.id);
			const price = new PriceInput(() => minorDigits, { optional: true });
			price.load(override?.priceMinor);
			shopsRows[shop.id] = {
				price,
				available: override?.available == null ? 'base' : override.available ? 'yes' : 'no',
			};
		}
		const price = new PriceInput(() => minorDigits);
		price.load(item?.priceMinor);
		itemForm = {
			id: item?.id ?? '',
			name: item?.name ?? '',
			price,
			available: item?.available ?? true,
			categoryId: item?.categoryId ?? category?.id ?? categories[0]?.id ?? '',
			groups: [...(item?.groups ?? [])],
			shops: shopsRows,
			soldBy: item?.soldBy ?? 'piece',
			description: item?.description ?? '',
			descriptionWas: item?.description ?? '',
			photoUrl: item?.photoUrl ?? '',
			photo: null,
			photoCleared: false,
		};
		itemFailure = '';
		photoProblem = null;
		flagged = [];
		checkFailed = false;
		itemOpen = true;
	}

	// The prices this save changes: the base price, and each shop whose own
	// price was set, changed or cleared, at what that shop will charge. A new
	// item replaces no price.
	function changedPrices(form: ItemForm, base: number): PriceEntry[] {
		const before = items.find((i) => i.id === form.id);
		if (!before) return [];
		const out: PriceEntry[] = [];
		if (base !== before.priceMinor) out.push({ itemId: form.id, priceMinor: base });
		for (const shop of shops.value) {
			const row = form.shops[shop.id];
			if (!row) continue;
			const own = (before.overrides ?? []).find((o) => o.shopId === shop.id)?.priceMinor ?? null;
			if ((row.price.minor ?? null) === own) continue;
			out.push({ itemId: form.id, shopId: shop.id, priceMinor: row.price.minor ?? base });
		}
		return out;
	}

	async function saveItem() {
		const form = itemForm;
		if (!form || !form.name.trim() || !form.categoryId || itemSaving) return;
		// Every price is checked before anything is sent, and says what is
		// wrong under its own field.
		const prices = [form.price, ...Object.values(form.shops).map((row) => row.price)];
		prices.forEach((price) => price.tidy());
		if (!prices.every((price) => price.valid) || form.price.minor === null) return;
		const asked = changedPrices(form, form.price.minor);
		if (asked.length > 0) {
			itemSaving = true;
			checkFailed = false;
			const answer = await checkPrices(asked);
			itemSaving = false;
			if ('failed' in answer) {
				checkFailed = true;
				return;
			}
			if (answer.flagged.length > 0) {
				flagged = answer.flagged;
				return;
			}
		}
		await commitItem();
	}

	async function commitItem() {
		const form = itemForm;
		if (!form || form.price.minor === null || itemSaving || preparing) return;
		const editing = form.id !== '';
		itemSaving = true;
		itemFailure = '';
		try {
			const body = {
				name: form.name.trim(),
				priceMinor: form.price.minor,
				available: form.available,
				categoryId: form.categoryId,
				...descriptionEdit(form.description, form.descriptionWas),
			};
			const saved = editing
				? await api.PATCH('/api/owner/menu/items/{itemId}', {
						params: { path: { itemId: form.id } },
						body,
					})
				: await api.POST('/api/owner/menu/items', {
						body: { ...body, soldBy: form.soldBy, sortOrder: shown.length * 10 },
					});
			if (saved.error !== undefined || !saved.data) {
				// A server just started holds new weighed items for a few
				// minutes (WeighedCreateHold), refused at soldBy.
				const held = !editing && saved.error?.details?.path === 'soldBy';
				itemFailure = held
					? message(language, 'menuWeighedSoon')
					: (saved.error?.message ?? message(language, 'loadFailed'));
				return;
			}
			const itemId = saved.data.id;
			const before = items.find((i) => i.id === itemId);
			// The item exists now: whatever fails from here, the sheet holds it
			// as an edit, so Save again changes it and never adds it twice.
			form.id = itemId;
			form.descriptionWas = form.description;

			// The options it offers: attach the new ones in their order, detach
			// the ones it no longer asks.
			const had = before?.groups ?? [];
			// A per-kg group attaches only to an item sold by weight (UC-10.R11);
			// the sheet offers none to a piece item, and a refusal at groupId,
			// from a menu that changed meanwhile, shows its own words.
			const asked = form.groups.filter((id) => offered(form.soldBy, id));
			for (const [index, groupId] of asked.entries()) {
				if (had[index] === groupId) continue;
				const attached = await api.PUT('/api/owner/menu/items/{itemId}/groups/{groupId}', {
					params: { path: { itemId, groupId } },
					body: { sortOrder: index * 10 },
				});
				if (attached.error !== undefined) {
					itemFailure =
						attached.error.details?.path === 'groupId'
							? (attached.error.message ?? message(language, 'loadFailed'))
							: message(language, 'loadFailed');
					await refresh();
					return;
				}
			}
			for (const groupId of had) {
				if (asked.includes(groupId)) continue;
				await api.DELETE('/api/owner/menu/items/{itemId}/groups/{groupId}', {
					params: { path: { itemId, groupId } },
				});
			}

			// Each shop's own price and availability, where it changed.
			if (editing) {
				for (const shop of shops.value) {
					const row = form.shops[shop.id];
					if (!row) continue;
					const override = (before?.overrides ?? []).find((o) => o.shopId === shop.id);
					const priceMinor = row.price.minor ?? undefined;
					const available = row.available === 'base' ? undefined : row.available === 'yes';
					if (override?.priceMinor === priceMinor && override?.available === available) continue;
					if (!override && priceMinor === undefined && available === undefined) continue;
					await api.PUT('/api/owner/menu/items/{itemId}/shop-prices/{shopId}', {
						params: { path: { itemId, shopId: shop.id } },
						body: {
							...(priceMinor !== undefined ? { priceMinor } : {}),
							...(available !== undefined ? { available } : {}),
						},
					});
				}
			}

			// The photo last, once the item has an id to hang it on. When it
			// fails the item is saved all the same, and the sheet stays open
			// on it, as an edit, with what went wrong.
			const sent = savePhoto(itemId, form.photo?.file ?? null, form.photoCleared, form.photoUrl);
			const outcome = photoOutcome(sent ? await sent : null);
			if (!outcome.close) {
				if ('problem' in outcome) {
					dropPicked();
					photoProblem = outcome.problem;
				} else {
					itemFailure = outcome.failure ?? message(language, 'loadFailed');
				}
				await refresh();
				return;
			}

			chosenCategory = form.categoryId;
			closeItem();
			await refresh();
			flash(message(language, 'saved'));
		} catch {
			itemFailure = message(language, 'loadFailed');
		} finally {
			itemSaving = false;
			// The price check's sheet closes whatever the save answered, so a
			// failure shows on the item's sheet beneath it.
			flagged = [];
		}
	}

	async function removeItem() {
		const form = itemForm;
		if (!form?.id || itemSaving) return;
		itemSaving = true;
		try {
			const { error } = await api.POST('/api/owner/menu/items/{itemId}/remove', {
				params: { path: { itemId: form.id } },
			});
			if (error !== undefined) {
				itemFailure = error.message ?? message(language, 'loadFailed');
				return;
			}
			closeItem();
			await refresh();
			flash(message(language, 'menuItemRemoved'));
		} catch {
			itemFailure = message(language, 'loadFailed');
		} finally {
			itemSaving = false;
		}
	}

	const itemTitle = $derived(
		itemForm?.id
			? `${message(language, 'menuEditTitle')} ${itemForm.name}`
			: message(language, 'menuAddItem'),
	);

	// A per-kg group is offered only to an item sold by weight (UC-10.R11).
	function offered(soldBy: 'piece' | 'weight', groupId: string): boolean {
		return soldBy === 'weight' || groups.find((g) => g.id === groupId)?.kind !== 'perKg';
	}

	const offeredGroups = $derived(
		itemForm ? groups.filter((g) => offered(itemForm?.soldBy ?? 'piece', g.id)) : [],
	);

	function groupNames(item: Item): string {
		return (item.groups ?? [])
			.map((id) => groups.find((g) => g.id === id)?.name)
			.filter(Boolean)
			.join(' · ');
	}

	// A weighed item's price is for a kilogram, and every list says so.
	function priceText(item: Item, minor: number): string {
		const text = formatMinor(minor, minorDigits);
		return item.soldBy === 'weight' ? fill(message(language, 'perKilo'), text) : text;
	}

	function shopCell(item: Item, shopId: string) {
		const override = (item.overrides ?? []).find((o) => o.shopId === shopId);
		return {
			price: override?.priceMinor != null ? priceText(item, override.priceMinor) : '',
			custom: override?.priceMinor != null,
			soldOut: override?.available === false,
		};
	}

	const columns = $derived([
		{ label: message(language, 'menuItem'), w: 'minmax(0,1.6fr)' },
		{ label: message(language, 'menuBase'), w: '112px', align: 'end' as const },
		{ label: message(language, 'menuStatus'), w: '104px' },
		...shops.value.map((shop) => ({ label: shop.name, w: 'minmax(0,1fr)', align: 'end' as const })),
		{ label: '', w: '64px' },
	]);
</script>

<div class="menu" data-testid="panel-menu">
	<Segmented
		label={message(language, 'ownerMenu')}
		options={[
			{ value: 'items', label: message(language, 'menuItems'), testId: 'menu-tab-items' },
			{
				value: 'options',
				label: message(language, 'menuOptions'),
				testId: 'menu-tab-options',
			},
		]}
		value={tab}
		wide
		onChange={(picked) => (tab = picked as 'items' | 'options')}
	/>

	{#if !menu.loaded || !shops.loaded}
		<div class="wait">
			<Loading
				size="lg"
				label={message(language, 'loading')}
				caption={message(language, 'loading')}
			/>
		</div>
	{:else if tab === 'options'}
		<MenuOptions {language} {minorDigits} />
	{:else}
		{#if failure || menu.failure}
			<Band tone="flag" testId="menu-error">{failure || menu.failure}</Band>
		{/if}
		<div class="items">
			<div class="side">
				<Card title={message(language, 'menuCategories')} testId="menu-categories">
					<div class="cats">
						{#if categories.length === 0 && !catEdit}
							<p class="soft pad" data-testid="menu-no-categories">
								{message(language, 'menuNoCategories')}
							</p>
						{/if}
						{#each [...categories, ...(catEdit?.id === 'new' ? [{ id: 'new', name: '', sortOrder: 0 }] : [])] as c, index (c.id)}
							{#if catEdit && catEdit.id === c.id}
								<div class="cat-edit" data-testid="category-form">
									<Field
										testId="category-name"
										ariaLabel={message(language, 'menuCategoryName')}
										value={catEdit.name}
										maxLength={120}
										onInput={(v) => catEdit && (catEdit.name = v)}
									/>
									<div class="row">
										<Button
											testId="category-save"
											label={message(language, 'save')}
											size="sm"
											disabled={!catEdit.name.trim() || catBusy}
											onPress={() => void saveCategory()}
										/>
										{#if c.id === 'new'}
											<Button
												testId="category-cancel"
												label={message(language, 'cancel')}
												tone="text-quiet"
												size="sm"
												onPress={() => (catEdit = null)}
											/>
										{:else}
											<Button
												testId="category-up"
												label={message(language, 'menuUp')}
												tone="text"
												size="sm"
												disabled={index === 0 || catBusy}
												onPress={() => void moveCategory(c, -1)}
											/>
											<Button
												testId="category-down"
												label={message(language, 'menuDown')}
												tone="text"
												size="sm"
												disabled={index === categories.length - 1 || catBusy}
												onPress={() => void moveCategory(c, 1)}
											/>
											<Button
												testId="category-remove"
												label={message(language, 'remove')}
												tone="text-quiet"
												size="sm"
												disabled={catBusy}
												onPress={() => removeCategory(c)}
											/>
										{/if}
									</div>
									{#if heldBy?.id === c.id}
										<p class="held" role="alert" data-testid="category-not-empty">
											{fill(message(language, 'menuCategoryNotEmpty'), heldBy.name, heldBy.count)}
										</p>
									{/if}
								</div>
							{:else}
								<NavItem
									testId="menu-category-{c.id}"
									label={c.name}
									meta={String(items.filter((i) => i.categoryId === c.id).length)}
									active={c.id === category?.id}
									ground="card"
									onPress={() => {
										chosenCategory = c.id;
										catEdit = null;
										heldBy = null;
									}}
								/>
							{/if}
						{/each}
						<div class="row top">
							<Button
								testId="menu-add-category"
								icon="plus"
								label={message(language, 'menuAddCategory')}
								tone="secondary"
								size="sm"
								onPress={() => (catEdit = { id: 'new', name: '' })}
							/>
							{#if category}
								<Button
									testId="menu-edit-category"
									icon="edit"
									label={message(language, 'edit')}
									tone="text"
									size="sm"
									onPress={() => category && (catEdit = { id: category.id, name: category.name })}
								/>
							{/if}
						</div>
					</div>
				</Card>
			</div>

			<div class="main">
				<div class="head">
					<span class="soft grow">
						{#if category}{shown.length} {message(language, 'menuItemsIn')} {category.name}{/if}
					</span>
					<Button
						testId="menu-add-item"
						icon="plus"
						label={message(language, 'menuAddItem')}
						tone="ink"
						size="sm"
						disabled={categories.length === 0}
						onPress={() => openItem(null)}
					/>
				</div>
				{#if shown.length === 0}
					<Card pad>
						<p class="empty" data-testid="menu-empty">
							{items.length === 0
								? message(language, 'menuEmpty')
								: message(language, 'menuCategoryEmpty')}
						</p>
					</Card>
				{:else}
					<TablePanel {columns} rowMin="var(--row-min-wide)" testId="menu-items">
						{#each shown as item (item.id)}
							<Row testId="menu-item-{item.id}">
								<span class="what">
									<!-- A sold-out name fades, so the eye finds it without reading
									     the status column. -->
									<span class="name" class:faded={!item.available}>{item.name}</span>
									{#if groupNames(item)}<span class="asks">{groupNames(item)}</span>{/if}
								</span>
								<span class="n strong end" dir="ltr" data-testid="menu-price-{item.id}">
									{priceText(item, item.priceMinor)}
								</span>
								<span class="cell">
									<Chip
										text={item.available
											? message(language, 'menuAvailable')
											: message(language, 'menuSoldOut')}
										tone={item.available ? 'ok' : 'flag'}
										size="md"
										dot
									/>
								</span>
								{#each shops.value as shop (shop.id)}
									{@const cell = shopCell(item, shop.id)}
									<!-- A shop's column speaks only when the shop overrides the
									     base; the status is said once, in its own column. -->
									<span class="shop" data-testid="menu-override-{item.id}-{shop.id}">
										{#if cell.custom}
											<span class="n strong" dir="ltr">{cell.price}</span>
										{:else if !cell.soldOut}
											<span class="soft">—</span>
										{/if}
										{#if cell.soldOut}
											<span class="out">{message(language, 'menuSoldOut')}</span>
										{/if}
									</span>
								{/each}
								<span class="edit">
									<Button
										testId="menu-edit-{item.id}"
										label={message(language, 'edit')}
										tone="text"
										size="sm"
										onPress={() => openItem(item)}
									/>
								</span>
							</Row>
						{/each}
					</TablePanel>
				{/if}
			</div>
		</div>
	{/if}
</div>

<Sheet
	open={itemOpen}
	covered={flagged.length > 0}
	title={itemTitle}
	primaryLabel={message(language, 'save')}
	primaryTestId="item-save"
	primaryDisabled={!itemForm?.name.trim() || !itemForm?.categoryId || preparing}
	primaryState={itemSaving ? 'pending' : 'idle'}
	pendingLabel={message(language, 'saving')}
	onPrimary={() => void saveItem()}
	secondaryLabel={itemForm?.id ? message(language, 'menuRemoveItem') : undefined}
	secondaryTestId="item-remove"
	onSecondary={() => void removeItem()}
	onClose={closeItem}
	closeLabel={message(language, 'sheetClose')}
	closeTestId="item-close"
	testId="item-form"
>
	{#if itemForm}
		<div class="sheet" data-scroll="">
			{#if checkFailed}
				<Band
					tone="flag"
					alert
					action={message(language, 'retry')}
					onAction={() => void saveItem()}
					actionTestId="price-check-retry"
					testId="price-check-failed"
				>
					{message(language, 'priceCheckFailed')}
				</Band>
			{/if}
			{#if itemFailure}
				<Band tone="flag" testId="item-error">{itemFailure}</Band>
			{/if}
			<div class="group">
				<span class="label">{message(language, 'menuSoldBy')}</span>
				{#if itemForm.id}
					<span data-testid="item-sold-by"
						>{message(
							language,
							itemForm.soldBy === 'weight' ? 'menuByWeight' : 'menuByPiece',
						)}</span
					>
				{:else}
					<Segmented
						label={message(language, 'menuSoldBy')}
						testId="item-sold-by"
						options={[
							{
								value: 'piece',
								label: message(language, 'menuByPiece'),
								testId: 'item-sold-by-piece',
							},
							{
								value: 'weight',
								label: message(language, 'menuByWeight'),
								testId: 'item-sold-by-weight',
							},
						]}
						value={itemForm.soldBy}
						onChange={(picked) =>
							itemForm && (itemForm.soldBy = picked === 'weight' ? 'weight' : 'piece')}
					/>
				{/if}
			</div>
			<div class="fields">
				<Field
					testId="item-name"
					label={message(language, 'menuItemName')}
					value={itemForm.name}
					maxLength={120}
					onInput={(v) => itemForm && (itemForm.name = v)}
				/>
				<Field
					testId="item-price"
					label={message(
						language,
						itemForm.soldBy === 'weight' ? 'menuPricePerKilo' : 'menuBasePrice',
					)}
					value={itemForm.price.text}
					note="{currency} · {message(language, 'menuPriceHint')}"
					error={itemForm.price.error(language)}
					errorTestId="item-price-error"
					inputMode="decimal"
					mono
					onInput={itemForm.price.set}
					onBlur={itemForm.price.tidy}
				/>
				<div class="group">
					<span class="label">{message(language, 'menuStatus')}</span>
					<Segmented
						label={message(language, 'menuStatus')}
						testId="item-available"
						options={[
							{
								value: 'yes',
								label: message(language, 'menuAvailable'),
								testId: 'item-available-yes',
							},
							{ value: 'no', label: message(language, 'menuSoldOut'), testId: 'item-available-no' },
						]}
						value={itemForm.available ? 'yes' : 'no'}
						onChange={(picked) => itemForm && (itemForm.available = picked === 'yes')}
					/>
				</div>
			</div>
			<Field
				testId="item-description"
				label={message(language, 'menuDescription')}
				value={itemForm.description}
				rows={3}
				maxCharacters={300}
				note={message(language, 'menuDescriptionNote')}
				onInput={(v) => itemForm && (itemForm.description = v)}
			/>
			<ItemPhoto
				{language}
				src={itemForm.photo?.url ?? (itemForm.photoCleared ? '' : itemForm.photoUrl)}
				problem={photoProblem}
				busy={itemSaving}
				{preparing}
				onPick={pickPhoto}
				onRemove={removePhoto}
			/>
			<div class="group">
				<span class="label">{message(language, 'menuCategory')}</span>
				<div class="picks">
					{#each categories as c (c.id)}
						<Button
							testId="item-category-{c.id}"
							label={c.name}
							tone={itemForm.categoryId === c.id ? 'ink' : 'secondary'}
							size="sm"
							onPress={() => itemForm && (itemForm.categoryId = c.id)}
						/>
					{/each}
				</div>
			</div>
			{#if offeredGroups.length > 0}
				<div class="group">
					<span class="label">{message(language, 'menuItemOptions')}</span>
					<div class="picks">
						{#each offeredGroups as g (g.id)}
							{@const on = itemForm.groups.includes(g.id)}
							<Button
								testId="item-group-{g.id}"
								label={g.name}
								tone={on ? 'ink' : 'secondary'}
								size="sm"
								onPress={() =>
									itemForm &&
									(itemForm.groups = on
										? itemForm.groups.filter((id) => id !== g.id)
										: [...itemForm.groups, g.id])}
							/>
						{/each}
					</div>
				</div>
			{/if}
			{#if itemForm.id}
				<div class="group">
					<span class="label">{message(language, 'menuOverrides')}</span>
					{#each shops.value as shop (shop.id)}
						{@const row = itemForm.shops[shop.id]}
						{#if row}
							<div class="shop-row">
								<span class="shop-name">{shop.name}</span>
								<div class="shop-price">
									<Field
										testId="override-{itemForm.id}-{shop.id}"
										ariaLabel={message(language, 'menuShopPrice')}
										value={row.price.text}
										placeholder={formatMinor(itemForm.price.minor ?? 0, minorDigits)}
										error={row.price.error(language)}
										inputMode="decimal"
										mono
										onInput={row.price.set}
										onBlur={row.price.tidy}
									/>
								</div>
								<Segmented
									label={message(language, 'menuShopAvailability')}
									options={[
										{
											value: 'base',
											label: message(language, 'menuUseBase'),
											testId: `override-base-${shop.id}`,
										},
										{
											value: 'yes',
											label: message(language, 'menuAvailable'),
											testId: `override-yes-${shop.id}`,
										},
										{
											value: 'no',
											label: message(language, 'menuSoldOut'),
											testId: `override-no-${shop.id}`,
										},
									]}
									value={row.available}
									onChange={(picked) => (row.available = picked as ShopRow['available'])}
								/>
							</div>
						{/if}
					{/each}
				</div>
			{/if}
		</div>
	{/if}
</Sheet>

<PriceCheck
	{language}
	{flagged}
	{wording}
	backLabel={itemTitle}
	saving={itemSaving}
	onBack={() => (flagged = [])}
	onConfirm={() => void commitItem()}
/>

<style>
	.menu {
		display: flex;
		flex-direction: column;
		gap: var(--space-9);
	}

	.wait {
		display: grid;
		place-items: center;
		min-block-size: 240px;
	}

	.items {
		display: flex;
		flex-wrap: wrap;
		gap: var(--grid-gap);
		align-items: flex-start;
	}

	.side {
		flex: 1 1 220px;
		min-inline-size: 0;
	}

	.cats {
		padding: var(--space-4);
		display: flex;
		flex-direction: column;
		gap: var(--space-1);
	}

	.cat-edit {
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
		padding: var(--space-3);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-input);
	}

	.row {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-2);
	}

	.held {
		margin: 0;
		font-size: var(--text-2xs);
		font-weight: 600;
		line-height: var(--leading-snug);
		color: var(--ink-flag-deep);
	}

	.row.top {
		padding-block-start: var(--space-3);
	}

	.main {
		flex: 999 1 480px;
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--grid-gap);
	}

	.head {
		display: flex;
		align-items: center;
		gap: var(--space-4);
	}

	.grow {
		flex: 1;
	}

	.soft {
		font-size: var(--text-sm);
		color: var(--text-muted);
	}

	.pad {
		margin: 0;
		padding: var(--space-4);
	}

	.empty {
		margin: 0;
		font-size: var(--text-md);
		color: var(--text-body);
	}

	.what {
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
	}

	.name {
		font-weight: 600;
	}

	.name.faded {
		color: var(--text-muted);
	}

	.asks {
		font-size: var(--text-micro);
		color: var(--text-muted);
	}

	.n {
		font-family: var(--font-mono);
	}

	.strong {
		font-weight: 700;
		color: var(--text-strong);
	}

	.end {
		min-inline-size: 0;
		text-align: end;
	}

	.cell {
		min-inline-size: 0;
		display: flex;
	}

	.shop {
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		gap: var(--space-1);
	}

	.out {
		font-size: var(--text-micro);
		font-weight: 600;
		color: var(--ink-flag-deep);
	}

	.edit {
		min-inline-size: 0;
		display: flex;
		justify-content: flex-end;
	}

	.sheet {
		flex: 1;
		min-block-size: 0;
		overflow-y: auto;
		overflow-x: hidden;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.fields {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr));
		gap: var(--space-7);
	}

	.group {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
	}

	.label {
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.picks {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-2);
	}

	.shop-row {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-4);
		padding: var(--space-3) 0;
		border-block-start: var(--border-width) solid var(--border-inset);
	}

	.shop-name {
		flex: 1 1 120px;
		min-inline-size: 0;
		font-size: var(--text-sm);
		font-weight: 600;
	}

	.shop-price {
		inline-size: 120px;
	}
</style>
